import { __assign, __extends } from "tslib";
import { BaseAdapter } from '../core/adapters/base-adapter';
import { MENU_CONSTANTS } from './menu-constants';
import { ListDropdown } from '../list-dropdown';
import { LIST_CONSTANTS, LIST_ITEM_CONSTANTS } from '../list';
var MenuAdapter = /** @class */ (function (_super) {
    __extends(MenuAdapter, _super);
    function MenuAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._childMenus = new Map();
        return _this;
    }
    MenuAdapter.prototype.getDropdownElement = function () {
        return this._listDropdown && this._listDropdown.dropdownElement ? this._listDropdown.dropdownElement : null;
    };
    MenuAdapter.prototype.hasTargetElement = function () {
        return !!this._targetElement;
    };
    MenuAdapter.prototype.initializeTarget = function () {
        this._targetElement = this._component.querySelector(MENU_CONSTANTS.selectors.TOGGLE);
        if (!this._targetElement) {
            this._targetElement = this._component.firstElementChild;
        }
    };
    MenuAdapter.prototype.initializeAccessibility = function (id) {
        if (!this._targetElement) {
            return;
        }
        this._targetElement.setAttribute('aria-atomic', 'true');
        this._targetElement.setAttribute('aria-live', 'assertive');
        this._targetElement.setAttribute('aria-haspopup', 'true');
        this._targetElement.setAttribute('aria-expanded', 'false');
        if (!this._targetElement.hasAttribute('aria-label')) {
            this._targetElement.setAttribute('aria-label', this._targetElement.textContent || '');
        }
    };
    MenuAdapter.prototype.addTargetListener = function (event, callback, bubbles) {
        if (bubbles === void 0) { bubbles = false; }
        if (this._targetElement) {
            this._targetElement.addEventListener(event, callback, bubbles);
        }
    };
    MenuAdapter.prototype.removeTargetListener = function (event, callback) {
        if (this._targetElement) {
            this._targetElement.removeEventListener(event, callback);
        }
    };
    MenuAdapter.prototype.attachMenu = function (config) {
        if (this._listDropdown || !this._targetElement) {
            return;
        }
        this._listDropdown = new ListDropdown(this._targetElement, config);
        this._listDropdown.open();
        this._targetElement.setAttribute('aria-expanded', 'true');
        this._targetElement.setAttribute('aria-controls', "list-dropdown-popup-" + config.id);
    };
    MenuAdapter.prototype.setOptions = function (options) {
        if (this._listDropdown) {
            this._listDropdown.setOptions(options);
        }
    };
    MenuAdapter.prototype.detachMenu = function () {
        if (this._targetElement) {
            this._targetElement.removeAttribute('aria-activedescendant');
            this._targetElement.removeAttribute('aria-expanded');
            this._targetElement.removeAttribute('aria-controls');
        }
        if (this._listDropdown) {
            this._listDropdown.close();
            this._listDropdown.destroy();
            this._listDropdown = undefined;
        }
    };
    MenuAdapter.prototype.setActiveOption = function (index) {
        if (this._listDropdown) {
            this._listDropdown.activateOption(index);
        }
    };
    MenuAdapter.prototype.activateFirstOption = function () {
        if (this._listDropdown) {
            this._listDropdown.activateFirstOption();
        }
    };
    MenuAdapter.prototype.getActiveOptionIndex = function () {
        return this._listDropdown ? this._listDropdown.getActiveOptionIndex() : -1;
    };
    MenuAdapter.prototype.focusTarget = function () {
        if (this._targetElement) {
            this._targetElement.focus();
        }
    };
    MenuAdapter.prototype.isTargetFocused = function () {
        return document.activeElement === this._targetElement;
    };
    MenuAdapter.prototype.updateActiveDescendant = function (id) {
        if (!this._targetElement) {
            return;
        }
        if (id) {
            this._targetElement.setAttribute('aria-activedescendant', id);
        }
        else {
            this._targetElement.removeAttribute('aria-activedescendant');
        }
    };
    MenuAdapter.prototype.addDropdownListener = function (type, listener) {
        if (!this._listDropdown || !this._listDropdown.dropdownElement) {
            return;
        }
        this._listDropdown.dropdownElement.addEventListener(type, listener);
    };
    MenuAdapter.prototype.isOwnElement = function (el) {
        if (!this._targetElement || !this._listDropdown || !this._listDropdown.dropdownElement) {
            return false;
        }
        return this._targetElement.contains(el) || !!this._listDropdown.dropdownElement.contains(el);
    };
    MenuAdapter.prototype.propagateKey = function (key) {
        if (this._listDropdown) {
            this._listDropdown.handleKey(key);
        }
    };
    MenuAdapter.prototype.proxyKeyboardEventToChild = function (evt, id) {
        if (!this.getDropdownElement()) {
            return;
        }
        var openMenu = this._getOpenChildMenu(id);
        if (openMenu) {
            openMenu.propagateKeyEvent(evt);
        }
    };
    MenuAdapter.prototype.toggleChildMenu = function (index, open) {
        var listItems = this._getListItems();
        var listItem = listItems[index];
        var childMenu = listItem.parentElement;
        if (childMenu && childMenu.tagName.toLowerCase() === MENU_CONSTANTS.elementName) {
            if (open === undefined) {
                childMenu.open = !childMenu.open;
            }
            else {
                childMenu.open = open;
            }
        }
    };
    MenuAdapter.prototype.createChildMenu = function (index, parentValue, openCb, closeCb, selectCb) {
        var _this = this;
        var menu = document.createElement('tcw-menu');
        menu.style.display = 'block';
        // Set listeners to notify the parent menu when anything important happens within a child menu that needs to be propagated to the root menu
        menu.addEventListener(MENU_CONSTANTS.events.OPEN, function () {
            _this._childMenus.set(index, menu);
            openCb(index);
        });
        menu.addEventListener(MENU_CONSTANTS.events.CLOSE, function () {
            _this._childMenus.delete(index);
            closeCb(index);
        });
        menu.addEventListener(MENU_CONSTANTS.events.SELECT, function (evt) {
            evt.stopPropagation();
            selectCb(__assign(__assign({}, evt.detail), { parentValue: parentValue }));
        });
        return menu;
    };
    MenuAdapter.prototype.closeOtherChildMenus = function (excludeIndex) {
        var _this = this;
        var menusToRemove = [];
        this._childMenus.forEach(function (menu, index) {
            if (index !== excludeIndex) {
                menu.open = false;
                menusToRemove.push(index);
            }
        });
        menusToRemove.forEach(function (index) { return _this._childMenus.delete(index); });
    };
    MenuAdapter.prototype.setSelectedValues = function (values) {
        if (this._listDropdown) {
            this._listDropdown.setSelectedValues(values);
        }
    };
    MenuAdapter.prototype._getOpenChildMenu = function (id) {
        if (!this.getDropdownElement()) {
            return null;
        }
        var list = this._getOwnList(id);
        return list ? list.querySelector(MENU_CONSTANTS.elementName + "[" + MENU_CONSTANTS.attributes.OPEN + "]") : null;
    };
    MenuAdapter.prototype._getOwnList = function (id) {
        var popup = this.getDropdownElement();
        if (!popup) {
            return null;
        }
        return popup.querySelector(LIST_CONSTANTS.elementName + "[id=\"list-dropdown-list-" + id + "\"]");
    };
    MenuAdapter.prototype._getListItems = function () {
        var popup = this.getDropdownElement();
        if (!popup) {
            return [];
        }
        var listElement = popup.querySelector(LIST_CONSTANTS.elementName);
        if (listElement) {
            return Array.from(listElement.querySelectorAll(LIST_ITEM_CONSTANTS.elementName));
        }
        return [];
    };
    return MenuAdapter;
}(BaseAdapter));
export { MenuAdapter };
